import { Floor } from "@pdfking/math"
import type { Expand, FileName, PartialExcept } from "@pdfking/structs"
import { ContentType } from "@pdfking/structs"
import { FromUnixTime, UnixTime, RecordAssign } from "@pdfking/tfunc"

import type { FileInfo } from "#public"

import { ContentETag } from "./ContentETag"

/** A Response that carries what it declares, without re-reading its headers.
 *
 * Everything written into the headers, kept on the object as well, so a caller
 * handed one knows the size, the etag, the type and the write time without
 * parsing them back out of a Headers somebody just wrote.
 *
 * Under `info` rather than spread across the response: `type` on a Response
 * already means the fetch mode ("basic", "cors"), and a content type sitting
 * where that is expected would be a lie to everyone who reads it. */
export type StandardResponse = Response & {
  info: FileInfo
}

/** A Response's init, plus what the thing it carries is.
 *
 * The four extras are the ones every reader here asks a response about — its
 * length, its type, its etag, when it was written — so they are said the way a
 * caller already holds them rather than as headers it has to spell. `headers`
 * still takes anything, and still wins.
 *
 * Only the size has no answer worth inventing — a length nobody knows is the
 * one thing a reader cannot recover, and guessing it from the body would mean
 * draining the body — so it is the one that must be said. */
export type ResponseCreateInit = Expand<ResponseInit & PartialExcept<FileInfo, "size">>

/** A Response that declares what it is carrying, and remembers it.
 *
 * Every store here builds one — prepared bytes, a stored handle, a file on
 * disk, a server answering a download — and ResponseAdopt, ResponseModifiedTime
 * and the rest parse them back, so the header names and their formats are
 * decided once here rather than kept in agreement in five places.
 *
 * The etag nobody wrote is {@link ContentETag} over the name, the length and
 * the write time — stable, so two reads of one unchanged entry answer with the
 * same one, which is the whole use of an etag and something a fresh uuid could
 * never be. A response with no name to give (a 404, a 204, a body that is a
 * report rather than an entry) is not an entry anybody caches, and derives the
 * one its blank name earns it. The write time is now, floored to the second,
 * because `last-modified` has no finer resolution and a response disagreeing
 * with its own header by 0.4s compares unequal to itself. */
export const ResponseCreate = (
  body: BodyInit | null | undefined,
  {
    modified = Floor(UnixTime()),
    name = "" as FileName,
    size,
    etag = ContentETag(size, modified, name),
    type = ContentType.BYTES,
    ...init
  }: ResponseCreateInit
): StandardResponse => {
  const headers = new Headers(init.headers)

  headers.set("content-length", `${size}`)
  headers.set("content-type", type)
  headers.set("etag", etag)
  headers.set("last-modified", FromUnixTime(modified).toUTCString())

  // The name, where there is one — this is the header a name travels in, and
  // ResponseFileName is what reads it back, so an entry keeps what it is
  // called across a download. `inline`, because saying a name is not the same
  // as asking a browser to save it. Encoded, so a path separator, a quote or
  // anything outside ASCII arrives as itself. A response with no name is not
  // an entry — a 404, a report — and says nothing here rather than saying it
  // is called "".
  if (name) {
    headers.set("content-disposition", `inline; filename*=UTF-8''${encodeURIComponent(name)}`)
  }

  return RecordAssign(new Response(body, { ...init, headers }), {
    info: { etag, modified, name, size, type },
  })
}
